import React from 'react';
import Icon from 'components/AppIcon';

const CopingMechanismsSection = ({ data = {}, onChange, errors = [] }) => {
  const handleMultiSelectChange = (field, value, checked) => {
    const currentValues = data[field] || [];
    const newValues = checked
      ? [...currentValues, value]
      : currentValues.filter(v => v !== value);
    onChange({ [field]: newValues });
  };

  const handleInputChange = (field, value) => {
    onChange({ [field]: value });
  };

  const copingStrategies = [
    { value: 'exercise', label: 'Regular exercise or physical activity', icon: 'Activity', category: 'Physical' },
    { value: 'meditation', label: 'Meditation or mindfulness practices', icon: 'Brain', category: 'Mental' },
    { value: 'breathing', label: 'Deep breathing exercises', icon: 'Wind', category: 'Mental' },
    { value: 'social-support', label: 'Talking to friends or family', icon: 'Users', category: 'Social' },
    { value: 'professional-help', label: 'Professional counseling or therapy', icon: 'UserCheck', category: 'Professional' },
    { value: 'hobbies', label: 'Engaging in hobbies or creative activities', icon: 'Palette', category: 'Creative' },
    { value: 'music', label: 'Listening to music or podcasts', icon: 'Music', category: 'Creative' },
    { value: 'reading', label: 'Reading books or articles', icon: 'Book', category: 'Mental' },
    { value: 'journaling', label: 'Writing or journaling', icon: 'PenTool', category: 'Mental' },
    { value: 'nature', label: 'Spending time in nature', icon: 'Trees', category: 'Physical' },
    { value: 'sleep', label: 'Maintaining good sleep hygiene', icon: 'Moon', category: 'Physical' },
    { value: 'nutrition', label: 'Healthy eating habits', icon: 'Apple', category: 'Physical' },
    { value: 'limit-news', label: 'Limiting news and social media', icon: 'Smartphone', category: 'Mental' },
    { value: 'routine', label: 'Maintaining daily routines', icon: 'Clock', category: 'Mental' },
    { value: 'prayer-spirituality', label: 'Prayer or spiritual practices', icon: 'Heart', category: 'Spiritual' },
    { value: 'none', label: 'I don\'t currently use coping strategies', icon: 'X', category: 'None' }
  ];

  const supportSources = [
    { value: 'family', label: 'Family members' },
    { value: 'friends', label: 'Close friends' },
    { value: 'partner', label: 'Romantic partner or spouse' },
    { value: 'colleagues', label: 'Work colleagues' },
    { value: 'therapist', label: 'Therapist or counselor' },
    { value: 'doctor', label: 'Primary care physician' },
    { value: 'support-groups', label: 'Support groups (online or in-person)' },
    { value: 'religious-community', label: 'Religious or spiritual community' },
    { value: 'online-communities', label: 'Online communities or forums' },
    { value: 'helplines', label: 'Crisis helplines or hotlines' },
    { value: 'none', label: 'I don\'t have support sources' }
  ];

  const substanceUse = [
    { value: 'no-change', label: 'No change in substance use' },
    { value: 'increased-alcohol', label: 'Increased alcohol consumption' },
    { value: 'increased-smoking', label: 'Increased smoking or vaping' },
    { value: 'increased-caffeine', label: 'Increased caffeine intake' },
    { value: 'prescription-misuse', label: 'Misuse of prescription medications' },
    { value: 'recreational-drugs', label: 'Use of recreational drugs' },
    { value: 'decreased-use', label: 'Decreased substance use' },
    { value: 'prefer-not-to-say', label: 'Prefer not to answer' }
  ];

  const copingEffectiveness = [
    { value: 'very-effective', label: 'Very Effective - Strategies work well for me' },
    { value: 'somewhat-effective', label: 'Somewhat Effective - Help but could be better' },
    { value: 'minimally-effective', label: 'Minimally Effective - Limited help' },
    { value: 'not-effective', label: 'Not Effective - Strategies don\'t help much' },
    { value: 'no-strategies', label: 'I don\'t currently use coping strategies' }
  ];

  const groupedStrategies = copingStrategies.reduce((acc, strategy) => {
    if (!acc[strategy.category]) {
      acc[strategy.category] = [];
    }
    acc[strategy.category].push(strategy);
    return acc;
  }, {});

  return (
    <div className="space-y-8">
      {/* Coping Strategies */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          What coping strategies do you currently use to manage stress and anxiety? *
        </label>
        <p className="text-sm text-text-secondary mb-4">
          Select all that apply. This helps us understand your current toolkit and suggest additional strategies.
        </p>
        
        {Object.entries(groupedStrategies).map(([category, strategies]) => (
          <div key={category} className="mb-6">
            <h4 className="text-sm font-medium text-text-primary mb-3 flex items-center">
              <div className="w-2 h-2 bg-primary rounded-full mr-2" />
              {category} Strategies
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-4">
              {strategies.map((strategy) => (
                <label
                  key={strategy.value}
                  className={`
                    flex items-center p-3 border rounded-lg cursor-pointer transition-all duration-150
                    ${(data.copingStrategies || []).includes(strategy.value)
                      ? 'border-primary bg-primary-50 text-primary-600' :'border-border bg-surface hover:bg-surface-secondary'
                    }
                  `}
                >
                  <input
                    type="checkbox"
                    checked={(data.copingStrategies || []).includes(strategy.value)}
                    onChange={(e) => handleMultiSelectChange('copingStrategies', strategy.value, e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`
                    w-4 h-4 border-2 rounded mr-3 flex items-center justify-center
                    ${(data.copingStrategies || []).includes(strategy.value)
                      ? 'border-primary bg-primary' :'border-border'
                    }
                  `}>
                    {(data.copingStrategies || []).includes(strategy.value) && (
                      <Icon name="Check" size={10} color="white" />
                    )}
                  </div>
                  <Icon name={strategy.icon} size={16} className="mr-2 flex-shrink-0" />
                  <span className="text-sm font-medium">{strategy.label}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Coping Effectiveness */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          How effective are your current coping strategies?
        </label>
        <div className="space-y-2">
          {copingEffectiveness.map((effectiveness) => (
            <label
              key={effectiveness.value}
              className={`
                relative flex items-center p-4 border rounded-lg cursor-pointer transition-all duration-150
                ${data.copingEffectiveness === effectiveness.value
                  ? 'border-secondary bg-secondary-50 text-secondary-600' :'border-border bg-surface hover:border-border-focus'
                }
              `}
            >
              <input
                type="radio"
                name="copingEffectiveness"
                value={effectiveness.value}
                checked={data.copingEffectiveness === effectiveness.value}
                onChange={(e) => handleInputChange('copingEffectiveness', e.target.value)}
                className="sr-only"
              />
              <div className={`
                w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center
                ${data.copingEffectiveness === effectiveness.value
                  ? 'border-secondary bg-secondary' :'border-border'
                }
              `}>
                {data.copingEffectiveness === effectiveness.value && (
                  <div className="w-2 h-2 rounded-full bg-white" />
                )}
              </div>
              <span className="text-sm font-medium">{effectiveness.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Support Sources */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          Who do you turn to for support during difficult times?
        </label>
        <div className="space-y-2">
          {supportSources.map((source) => (
            <label
              key={source.value}
              className="flex items-center p-3 border border-border rounded-lg hover:bg-surface-secondary cursor-pointer transition-all duration-150"
            >
              <input
                type="checkbox"
                checked={(data.supportSources || []).includes(source.value)}
                onChange={(e) => handleMultiSelectChange('supportSources', source.value, e.target.checked)}
                className="w-4 h-4 text-accent border-border rounded focus:ring-accent focus:ring-2"
              />
              <span className="ml-3 text-sm text-text-primary">{source.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Substance Use */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          Has your use of substances changed during the pandemic?
        </label>
        <p className="text-sm text-text-secondary mb-4">
          This information helps us provide appropriate resources and support.
        </p>
        <div className="space-y-2">
          {substanceUse.map((use) => (
            <label
              key={use.value}
              className={`
                relative flex items-center p-4 border rounded-lg cursor-pointer transition-all duration-150
                ${data.substanceUse === use.value
                  ? 'border-accent bg-accent-50 text-accent-600' :'border-border bg-surface hover:border-border-focus'
                }
              `}
            >
              <input
                type="radio"
                name="substanceUse"
                value={use.value}
                checked={data.substanceUse === use.value}
                onChange={(e) => handleInputChange('substanceUse', e.target.value)}
                className="sr-only"
              />
              <div className={`
                w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center
                ${data.substanceUse === use.value
                  ? 'border-accent bg-accent' :'border-border'
                }
              `}>
                {data.substanceUse === use.value && (
                  <div className="w-2 h-2 rounded-full bg-white" />
                )}
              </div>
              <span className="text-sm font-medium">{use.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* New Strategies Interest */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          Are you interested in learning new coping strategies?
        </label>
        <div className="space-y-2">
          {[
            { value: 'very-interested', label: 'Very interested - I\'d like to try new approaches' },
            { value: 'somewhat-interested', label: 'Somewhat interested - Open to suggestions' },
            { value: 'not-sure', label: 'Not sure - Depends on the strategy' },
            { value: 'not-interested', label: 'Not interested - Happy with current strategies' }
          ].map((interest) => (
            <label
              key={interest.value}
              className={`
                relative flex items-center p-4 border rounded-lg cursor-pointer transition-all duration-150
                ${data.newStrategiesInterest === interest.value
                  ? 'border-primary bg-primary-50 text-primary-600' :'border-border bg-surface hover:border-border-focus'
                }
              `}
            >
              <input
                type="radio"
                name="newStrategiesInterest"
                value={interest.value}
                checked={data.newStrategiesInterest === interest.value}
                onChange={(e) => handleInputChange('newStrategiesInterest', e.target.value)}
                className="sr-only"
              />
              <div className={`
                w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center
                ${data.newStrategiesInterest === interest.value
                  ? 'border-primary bg-primary' :'border-border'
                }
              `}>
                {data.newStrategiesInterest === interest.value && (
                  <div className="w-2 h-2 rounded-full bg-white" />
                )}
              </div>
              <span className="text-sm font-medium">{interest.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Additional Coping Information */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Additional Information About Your Coping Methods (Optional)
        </label>
        <textarea
          value={data.additionalCopingInfo || ''}
          onChange={(e) => handleInputChange('additionalCopingInfo', e.target.value)}
          placeholder="Share any other coping strategies you use, challenges you face, or specific areas where you'd like more support..."
          rows={4}
          className="form-input resize-none"
        />
      </div>

      {/* Wellness Resources */}
      <div className="bg-success-50 border border-success-100 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Heart" size={20} color="#10B981" className="mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-success-600 mb-2">
              Building Your Wellness Toolkit
            </h3>
            <p className="text-sm text-success-600 mb-2">
              Effective coping strategies are personal and may change over time. What works for others might not work for you, and that's perfectly normal.
            </p>
            <ul className="text-sm text-success-600 space-y-1">
              <li>• Experiment with different strategies to find what works best</li>
              <li>• Combine multiple approaches for maximum effectiveness</li>
              <li>• Be patient with yourself as you develop new habits</li>
              <li>• Don't hesitate to seek professional help when needed</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CopingMechanismsSection;